import React from 'react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PageHero } from '../components/PageHero';
import { Wrench, Crosshair, Target, Phone, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { PHONE, PHONE_TEL } from '../constants/business';
export function Gunsmithing() {
  useDocumentMeta('Gunsmithing Services | Kjergaard Sports', 'In-house gunsmithing, barrel threading, scope mounting, stock work, and firearm customization at Kjergaard Sports in Lake Benton, Minnesota.');
  return <div data-id="element-674">
      <PageHero title="Gunsmithing Services" image="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Gunsmithing workshop with mill and lathe at Kjergaard Sports" data-id="element-675" />

      <section className="py-20 bg-brand-offwhite" data-id="element-676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-677">
          <div className="max-w-3xl mb-16" data-id="element-678">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-679">
              In-House Gunsmithing Support
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-680">
              Kjergaard Sports maintains an in-house gunsmithing shop to support
              customers with everything from routine cleanings to precision
              barrel work. Our shop is built for real, hands-on help — not a
              counter that ships your firearm out and waits.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-681">
            <div data-id="element-682">
              <div className="flex items-center space-x-3 mb-4" data-id="element-683">
                <div className="h-px w-8 bg-brand-leather" data-id="element-684"></div>
                <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-685">
                  A signature capability
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-686">
                Barrel Threading
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-4 leading-relaxed" data-id="element-687">
                Barrel threading is one of the areas where our shop has built
                strong in-house capability. Nick's barrel threading work is a
                major part of what we can offer customers looking for precise,
                clean, dependable gunsmithing support.
              </p>
              <p className="text-base text-brand-charcoal/75 leading-relaxed" data-id="element-688">
                Whether you're prepping for a suppressor or upgrading a rifle,
                stop in and we can walk you through options, lead times, and
                what to expect.
              </p>
            </div>
            <div data-id="element-689">
              <img src="https://cdn.magicpatterns.com/uploads/6uBKPbVFcv4swNygT9aBpc/services-gunsmithing-workshop.jpg" alt="Mill, lathe, and gunsmithing workshop at Kjergaard Sports" className="w-full h-auto rounded-sm shadow-lg object-cover" data-id="element-690" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-bone" data-id="element-691">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-692">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-12 text-center" data-id="element-693">
            Scope Mounting & Setup
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-694">
            {[{
            icon: <Crosshair className="w-8 h-8 text-brand-hunter" data-id="element-695" />,
            title: 'Scope Mounting',
            copy: 'Bring in your scope, rings, and rifle — we can mount and torque to spec.'
          }, {
            icon: <Target className="w-8 h-8 text-brand-hunter" data-id="element-696" />,
            title: 'Ring & Base Selection',
            copy: 'Not sure what rings or bases you need? Stop in for guidance on the right setup.'
          }, {
            icon: <Wrench className="w-8 h-8 text-brand-hunter" data-id="element-697" />,
            title: 'Boresighting & Setup',
            copy: 'Boresighting and sight-in guidance to get you on paper before you hit the range.'
          }].map((item, i) => <div key={i} className="bg-brand-offwhite p-8 rounded-sm border border-brand-cream" data-id="element-698">
                <div className="w-14 h-14 bg-brand-bone rounded-full flex items-center justify-center mb-5" data-id="element-699">
                  {item.icon}
                </div>
                <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-700">
                  {item.title}
                </h3>
                <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-701">
                  {item.copy}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-offwhite" data-id="element-702">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-703">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-12" data-id="element-704">
            Cleaning, Repair & Stock Work
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-705">
            {[{
            title: 'Basic Cleaning',
            copy: 'Routine cleanings to keep your firearm running reliably.'
          }, {
            title: 'Basic Repair',
            copy: 'Common repair work for everyday hunting and sporting rifles, shotguns, and handguns.'
          }, {
            title: 'Stock Work & Customization',
            copy: 'Stock work and firearm customization to fit your build and intended use.'
          }].map((item, i) => <div key={i} className="bg-brand-cream p-8 rounded-sm border border-brand-bone" data-id="element-706">
                <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-707">
                  {item.title}
                </h3>
                <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-708">
                  {item.copy}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-cream" data-id="element-709">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-710">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-711">
            New Equipment, Expanded Capability
          </h2>
          <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-712">
            Kjergaard Sports recently invested in a high-end mill and lathe to
            expand what we can take on in-house. That investment lets us handle
            more precise work locally instead of sending it elsewhere.
          </p>
        </div>
      </section>

      <section className="py-20 bg-brand-offwhite" data-id="element-713">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-714">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-715">
            Call Before You Bring It In
          </h2>
          <p className="text-lg text-brand-charcoal/80 mb-10 leading-relaxed" data-id="element-716">
            Gunsmithing turnaround varies with workload and parts availability.
            Call ahead so we can talk through your project, what to bring, and
            current lead times.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8" data-id="element-717">
            <a href={PHONE_TEL} className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-718">
              <Phone className="w-5 h-5 mr-2" data-id="element-719" />
              Call {PHONE}
            </a>
            <Link to="/contact" className="border-2 border-brand-hunter text-brand-hunter hover:bg-brand-hunter hover:text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-720">
              Ask About Gunsmithing
            </Link>
            <Link to="/contact" className="border border-brand-charcoal/20 hover:border-brand-leather text-brand-charcoal px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-721">
              Plan Your Visit
            </Link>
          </div>

          <div className="bg-brand-cream border-l-4 border-brand-leather p-6 text-left max-w-2xl mx-auto" data-id="element-722">
            <div className="flex items-start" data-id="element-723">
              <Info className="w-5 h-5 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-724" />
              <p className="text-brand-charcoal/85 text-sm leading-relaxed" data-id="element-725">
                Information on this page is general information only and is not
                legal advice. Requirements can vary. Please call the store with
                questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-726" />
    </div>;
}